import {
  Brush,
  Delete,
  Description,
  Download,
  Edit,
  Folder,
  History,
  InsertDriveFileOutlined,
  OpenInNew,
  Save,
  Slideshow,
  TableChart,
  Upload,
} from "@mui/icons-material";
import {
  Avatar,
  CircularProgress,
  Collapse,
  FormHelperText,
  IconButton,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";

import { processesUploadsApi, uploadsApi } from "api";
import { useSnackbar, useTranslations } from "hooks";
import React, { useEffect, useState } from "react";
import { getTranslation } from "common";
import {
  SUPPORTED_EXTENSIONS,
  formatDate,
  getFileExtension,
  toBase64,
} from "common/utilities";
import { Box } from "@mui/system";
import { set } from "lodash";

export interface Attachment {
  id: number;
  idorganization: number;
  filename: string;
  description: string;
  idtype: number;
  cloudlink: string;
  creation: string;
  last_modified: string | null;
  foldername: string;
  username: string;
}

interface AttchmentListProps {
  attachments: Attachment[];
  canManage?: boolean;
  knownWidth: number;
  horizontal?: boolean;
  getAttachments: () => Promise<void>;
  images: { [key: string]: string };
  histories: { [key: string]: any };
}

const AttachmentList: React.FC<AttchmentListProps> = ({
  attachments,
  knownWidth,
  horizontal,
  getAttachments,
  images,
  histories,
  canManage = false,
}) => {

  const [idItem, setIdItem] = useState<number | null>(null);
  const download = async (attachment: Attachment) => {
    setLoading(true);
    setIdItem(attachment.id);
    try {
      const { data } = await processesUploadsApi.get(`/${attachment.id}`);
      const a = document.createElement("a");
      a.href = data.blob;
      a.download = data.filename;
      a.target = "_blank";
      document.body.appendChild(a);
      a.click();
    } catch {
    } finally {
      setLoading(false);
    }
  };

  let organizationId: number | null | undefined = null;
  const { sendSnack } = useSnackbar();
  const translations = useTranslations();
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [edit, setEdit] = useState<Attachment | null>(null);

  useEffect(() => {
    if (edit) {
      document.getElementById(`input-description-${edit.id}`)?.focus();
    }
  }, [edit]);

  const handleDeleteFile = async (attachment: Attachment) => {
    try {
      const deleteApi = await processesUploadsApi.delete(`/${attachment.id}`);
      sendSnack({
        message: `${deleteApi.data.responsemessage}`,
        type: "success",
      });
      getAttachments();
    } catch {
    } finally {
    }
  };

  const handleUpdateFile = async (
    attachment: Attachment,
    files: FileList | null
  ) => {
    setLoading(true);
    try {
      if (files && files.length > 0) {
        const file = files[0];
        const payload = {
          idorganization: organizationId,
          filename: file.name,
          cloudlink: "",
          idtype: 1,
          description: file.name,
          blob: await toBase64(file),
        };
        setLoading(true);
        setIdItem(attachment.id);
        try {
          const updateApi = await uploadsApi.put(`/${attachment.id}`, payload);
          sendSnack({
            message: `${updateApi.data.responsemessage}`,
            type: "success",
          });
        } catch {
        } finally {
          setLoading(false);
        }
        getAttachments();
      }
    } finally {
    }
  };

  const isFromGoogleDrive = (idtype: number) => {
    return [20, 21, 22, 23, 24, 25].includes(idtype);
  };

  const saveEdit = async (e?: any) => {
    if (e?.preventDefault) e.preventDefault();
    if (e?.stopPropagation) e.stopPropagation();

    if (!edit) return;

    setLoading(true);
    setIdItem(edit.id || null);

    try {
      await processesUploadsApi.put(`${edit.id}`, {
        // filename: edit.filename,
        description: edit.description,
      });
      
      getAttachments();
      setEdit(null);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };


  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        e.stopPropagation();
        setEdit(null);       // ❌ annulla edit
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [setEdit, saveEdit]);



  return horizontal ? (
    <Stack direction="row" spacing={1} flex={1}>
      <Stack
        direction="row"
        position="relative"
        sx={{
          overflowX: "auto",
        }}
        width="100%"
      >
        {attachments
          .sort(
            (a, b) =>
              new Date(b.creation).getTime() - new Date(a.creation).getTime()
          )
          .map((attachment) => (
            <Tooltip
              key={`horizontal-attachment-${attachment.id}`}
              title={attachment.filename}
            >
              <Stack maxWidth={50} direction="column" alignItems="center">
                <FormHelperText
                  id="attachment-list"
                  sx={{
                    textOverflow: "ellipsis",
                    maxWidth: 50,
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                  }}
                >
                  {attachment.filename}
                </FormHelperText>
              </Stack>
            </Tooltip>
          ))}
      </Stack>
    </Stack>
  ) : (
    <Stack direction="column" spacing={1} flex={1} width="100%">
      <Stack
        component={List}
        direction="column"
        position="relative"
        width="100%"
      >
        {attachments
          .sort(
            (a, b) =>
              new Date(b.creation).getTime() - new Date(a.creation).getTime()
          )
          .map((attachment) => (
            <React.Fragment key={`vertical-attachment-${attachment.id}`}>
              <ListItem key={`attachment-${attachment.id}`} id={`attachment-${attachment.id}`} dense>
                <ListItemAvatar>
                  <Avatar
                    sx={{ fontSize: "0.8rem" }}
                    alt="..."
                    src={
                      SUPPORTED_EXTENSIONS.includes(
                        getFileExtension(attachment.filename) || ""
                      )
                        ? images[attachment.id]
                        : undefined
                    }
                  >
                    {attachment.idtype === 21 ? (
                      <Folder />
                    ) : attachment.idtype === 22 ? (
                      <Description />
                    ) : attachment.idtype === 23 ? (
                      <TableChart />
                    ) : attachment.idtype === 24 ? (
                      <Slideshow />
                    ) : attachment.idtype === 25 ? (
                      <Brush />
                    ) : (
                      <InsertDriveFileOutlined />
                    )}
                  </Avatar>
                </ListItemAvatar>
                <ListItemText
                  sx={{
                    textOverflow: "ellipsis",
                    maxWidth: "auto",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                  }}
                  primary={
                    <Box display="flex" flexDirection="column">
                      {/* Prima riga: input descrizione + tutte le icone */}
                      <Stack
                        component="form"
                        direction="row"
                        alignItems="center"
                        spacing={3}
                      >
                        <Box flex={1} minWidth={0}>
                          <input
                            id={`input-description-${attachment.id}`}
                            style={{
                              border: "none",
                              background: "transparent",
                              width: "100%",
                              fontSize: "16px",
                              fontWeight: 600,
                              fontFamily: "Poppins-Light",
                            }}
                            value={
                              edit?.id !== attachment.id
                                ? attachment.description
                                : edit.description
                            }
                            onChange={({ target }) =>
                              setEdit((prev) => ({
                                ...(prev as Attachment),
                                description: target.value,
                              }))
                            }
                            disabled={edit?.id !== attachment.id}
                            onBlur={() => {
                              // Salva solo se stiamo editando questo attachment
                              if (edit?.id === attachment.id) {
                                saveEdit({});
                              }
                            }}
                          />
                        </Box>


                        {/* Edit/Save */}
                        {canManage && (
                          <Tooltip
                            title={
                              edit?.id !== attachment.id
                                ? getTranslation(translations, "pm.button.edit")
                                : getTranslation(translations, "pm.button.save")
                            }
                          >
                            <IconButton
                              onClick={(e) => {
                                e.preventDefault();
                                edit?.id !== attachment.id
                                  ? setEdit(attachment)
                                  : saveEdit(e);
                              }}
                              size="medium"
                              type={
                                edit?.id !== attachment.id ? "button" : "submit"
                              }
                              sx={{ p: 0.25 }}
                            >
                              {edit?.id !== attachment.id ? (
                                <Edit fontSize="inherit" />
                              ) : (
                                <Save fontSize="inherit" />
                              )}
                            </IconButton>
                          </Tooltip>
                        )}

                        {/* Delete */}
                        {canManage && (
                          <Tooltip
                            title={getTranslation(
                              translations,
                              "settings.delete.label"
                            )}
                          >
                            <IconButton
                              id={`delete-attachment-${attachment.id}`}
                              size="medium"
                              onClick={(e) => {
                                e.stopPropagation();
                                e.preventDefault();
                                handleDeleteFile(attachment);
                              }}
                              sx={{ p: 0.25 }}
                            >
                              <Delete fontSize="inherit" />
                            </IconButton>
                          </Tooltip>
                        )}

                        {/* Google Drive: Open in new */}
                        {isFromGoogleDrive(attachment.idtype) ? (
                          <Tooltip
                            title={getTranslation(
                              translations,
                              "attachment.view.ondrive"
                            )}
                          >
                            <IconButton
                              id={`open-attachment-${attachment.id}`}
                              size="medium"
                              component="a"
                              href={attachment.cloudlink}
                              target="_blank"
                              sx={{ p: 0.25 }}
                            >
                              <OpenInNew fontSize="inherit" />
                            </IconButton>
                          </Tooltip>
                        ) : (
                          <>
                            {/* Upload new version */}
                            {canManage && !attachment.cloudlink && (
                              <Tooltip
                                title={getTranslation(
                                  translations,
                                  "attachments.new_version.label"
                                )}
                              >
                                <IconButton
                                  id={`modify-attachment-${attachment.id}`}
                                  size="medium"
                                  component="label"
                                  sx={{ p: 0.25 }}
                                >
                                  <Upload fontSize="inherit" />
                                  <input
                                    id={`modify-attachment-${attachment.id}-input`}
                                    type="file"
                                    hidden
                                    onChange={({ target }) => {
                                      handleUpdateFile(attachment, target.files);
                                    }}
                                  />
                                </IconButton>
                              </Tooltip>
                            )}

                            {/* Download */}
                            <Tooltip
                              title={getTranslation(
                                translations,
                                "settings.download.label"
                              )}
                            >
                              <IconButton
                                id={`download-attachment-${attachment.id}`}
                                size="medium"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  e.preventDefault();
                                  download(attachment);
                                }}
                                sx={{ p: 0.25 }}
                              >
                                <Download fontSize="inherit" />
                              </IconButton>
                            </Tooltip>
                          </>
                        )}

                        {/* History */}
                        {histories[attachment.id].length > 0 && (
                          <Tooltip
                            title={getTranslation(
                              translations,
                              "settings.histories.label"
                            )}
                          >
                            <IconButton
                              id={`history-button-${attachment.id}`}
                              size="medium"
                              onClick={() => {
                                setSelectedId(
                                  selectedId === attachment.id
                                    ? null
                                    : attachment.id
                                );
                              }}
                              sx={{ p: 0.25 }}
                            >
                              <History fontSize="inherit" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Stack>

                      {/* Seconda riga: username e data */}
                      <Typography
                        variant="body2"
                        component="span"
                        sx={{ color: "text.secondary" }}
                      >
                        {attachment.username}, {formatDate(attachment.creation)}
                      </Typography>
                    </Box>
                  }
                />
                {loading && idItem == attachment.id && (
                  <Stack alignItems="center">
                    <CircularProgress />
                  </Stack>
                )}
              </ListItem>

              {/* History collapse */}
              {histories[attachment.id].length > 0 && (
                <Collapse in={selectedId === attachment.id}>
                  <List sx={{ width: "100%", pl: 4 }} dense>
                    {histories[attachment.id].map(
                      (history: any, index: any) => (
                        <ListItem
                          id={`attachment-history-${history.id}`}
                          key={index}
                        >
                          <ListItemText
                            sx={{
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                            }}
                            primary={`${getTranslation(
                              translations,
                              "settings.filename.label"
                            )}: ${history.filename}`}
                            secondary={`${getTranslation(
                              translations,
                              "settings.creation.label"
                            )}: ${history.nameuser}, ${formatDate(
                              history.creation
                            )}`}
                          />
                          <Tooltip
                            title={getTranslation(
                              translations,
                              "settings.download.label"
                            )}
                          >
                            <IconButton
                              id={`download-history-${history.id}`}
                              size="medium"
                              onClick={(e) => {
                                e.stopPropagation();
                                e.preventDefault();
                                download(history);
                              }}
                              sx={{ p: 0.25 }}
                            >
                              <Download fontSize="inherit" />
                            </IconButton>
                          </Tooltip>
                        </ListItem>
                      )
                    )}
                  </List>
                </Collapse>
              )}

            </React.Fragment>
          ))}

      </Stack>
    </Stack>
  );
};

export default AttachmentList;